import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleTrackingWorkflows,
  getPlants,
  getUnitOfMeasurements,
} from 'src/redux/Actions/VehicleTracking';
import {
  getVehicleDropdownOptions,
  getTransporterDropdownOptions,
} from 'src/redux/Actions/VehicleTrackingOutward';
import TabCreateView from 'src/components/Layout/TabCreateView';
import BasicDetails from 'src/components/CreateDuplicateVehicleTracking/BasicDetails';
import AdditionalDetails from 'src/components/CreateDuplicateVehicleTracking/AdditionalDetails';
import 'src/style/createVehicleTracking.css';

function VehicleTrackingDetails({
  formData,
  handleOnChange,
  setFormData,
  errorField,
  mobileNumberError,
  selectedStep,
  setSelectedStep,
  totalSteps,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const [transporterSearchTerm, setTransporterSearchTerm] = useState('');
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');

  // Owns its own editing state now — this step toggles between a
  // read-only view and an edit view instead of always being editable.
  const [isEditing, setIsEditing] = useState(false);

  const tenantOrganisationId = authReducer.userData.organisations?.id;

  const availableWorkflows = vehicleTrackingDataReducer.workflows;
  const { plantsData, unitOfMeasurements } = vehicleTrackingDataReducer;
  const { transporterDropdownOptions, vehicleDropdownOptions } =
    VehicleTrackingOutwardReducer;
  const [unitOfMeasurementSearchTerm, setUnitOfMeasurementSearchTerm] =
    useState('');

  const choices = [
    { id: 1, name: 'Average' },
    { id: 2, name: 'Good' },
    { id: 3, name: 'Excellent' },
  ];

  const isCreatingNewTransporter =
    formData?.transporterId === null && formData?.transporterName;
  const isCreatingNewVehicle =
    formData?.selectedVehicleOption?.value === null && formData?.vehicleNumber;
  const isTransporterInvalid =
    !formData?.transporterId && !formData?.transporterName;
  const isVehicleFormFieldEmpty =
    !formData?.selectedVehicleOption?.value && !formData?.vehicleNumber;

  // If navigation moves away from this step while it's mid-edit,
  // drop back to view mode so returning to it doesn't reopen the form.
  useEffect(() => {
    if (selectedStep !== 1 && isEditing) {
      setIsEditing(false);
    }
  }, [selectedStep]);

  function handleEdit() {
    setIsEditing(true);
  }

  function handleCancelEdit() {
    setIsEditing(false);
  }

  function handleSubmitEdit() {
    setIsEditing(false);
  }

  function handleNextStep() {
    setSelectedStep(2);
  }

  // Transporter/vehicle search dropdowns are only needed while actively
  // editing — in view mode we already have the names in formData.
  useEffect(() => {
    if (!isEditing) return undefined;

    const vehicleId =
      formData?.selectedVehicleOption?.value && formData?.vehicleNumber
        ? formData.selectedVehicleOption.value
        : null;

    dispatch(
      getTransporterDropdownOptions(
        tenantOrganisationId,
        transporterSearchTerm,
        vehicleId,
      ),
    );
    return undefined;
  }, [
    dispatch,
    isEditing,
    tenantOrganisationId,
    transporterSearchTerm,
    formData?.selectedVehicleOption,
  ]);

  const fetchVehicleDropdownOptions = useCallback(() => {
    const transporterId = formData?.transporterId || null;

    dispatch(
      getVehicleDropdownOptions(
        tenantOrganisationId,
        vehicleSearchTerm,
        transporterId,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    vehicleSearchTerm,
    formData?.transporterId,
  ]);

  useEffect(() => {
    if (!isEditing) return;
    fetchVehicleDropdownOptions();
  }, [isEditing, fetchVehicleDropdownOptions]);

  useEffect(() => {
    if (tenantOrganisationId) {
      dispatch(getVehicleTrackingWorkflows(tenantOrganisationId, '2')); // hardcoded to outward
      dispatch(getPlants(tenantOrganisationId));
    }
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    dispatch(
      getUnitOfMeasurements(tenantOrganisationId, unitOfMeasurementSearchTerm),
    );
  }, [dispatch, tenantOrganisationId, unitOfMeasurementSearchTerm]);

  let vehicleNumberDataOption = [],
    businessPartnerDataOption = [];
  if (vehicleDropdownOptions && vehicleDropdownOptions.length > 0) {
    vehicleNumberDataOption = vehicleDropdownOptions.map((vehicleObj) => ({
      value: vehicleObj.id,
      label: vehicleObj.vehicle_number,
    }));
  } else if (
    vehicleSearchTerm &&
    vehicleDropdownOptions &&
    vehicleDropdownOptions.length === 0
  ) {
    vehicleNumberDataOption.push({
      value: `create-${vehicleSearchTerm}`,
      label: (
        <div className="d-flex justify-content-between align-items-center text-blue">
          <span>Create vehicle</span>
          <span>+</span>
        </div>
      ),
      isCreateOption: true,
    });
  }

  if (transporterSearchTerm && transporterDropdownOptions?.length === 0) {
    businessPartnerDataOption.push({
      value: `create-${transporterSearchTerm}`,
      label: (
        <div className="d-flex justify-content-between align-items-center text-blue">
          <span>Create transporter</span>
          <span>+</span>
        </div>
      ),
      isCreateOption: true,
    });
  } else if (
    transporterDropdownOptions &&
    transporterDropdownOptions.length > 0
  ) {
    businessPartnerDataOption = transporterDropdownOptions.map(
      (transporterObj) => ({
        value: transporterObj.id,
        label: transporterObj.name,
      }),
    );
  }

  const handleVehicleChange = (selectedOption) => {
    const newVehicleNumber = vehicleSearchTerm;
    setVehicleSearchTerm('');

    if (!selectedOption) {
      setFormData((prev) => ({
        ...prev,
        selectedVehicleOption: null,
        vehicleNumber: '',
      }));
      return;
    }

    if (selectedOption.isCreateOption) {
      setFormData((prev) => ({
        ...prev,
        selectedVehicleOption: {
          value: null,
          label: newVehicleNumber,
        },
        vehicleNumber: newVehicleNumber,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      selectedVehicleOption: {
        value: selectedOption.value,
        label: selectedOption.label,
      },
      vehicleNumber: selectedOption.label,
      transporterId: prev.transporterId,
      transporterName: prev.transporterName,
    }));
  };

  const plantDataOption =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowDataOption =
    availableWorkflows?.map((availableWorkflow) => ({
      value: availableWorkflow.id,
      label: availableWorkflow.name,
    })) || [];

  const unitOfMeasurementOptions = unitOfMeasurements?.map((unitObj) => ({
    label: unitObj.name,
    value: unitObj.id,
  }));

  const transporterValue = (() => {
    if (isCreatingNewTransporter) {
      return {
        label: formData.transporterName,
        value: null,
      };
    }

    const existingOption = businessPartnerDataOption.find(
      (option) => option.value === formData?.transporterId,
    );

    return existingOption || '';
  })();

  const vehicleValue = (() => {
    if (!formData?.selectedVehicleOption) return '';

    if (isCreatingNewVehicle) {
      return {
        label: formData.vehicleNumber,
        value: null,
      };
    }

    return (
      vehicleNumberDataOption.find(
        (option) => option.value === formData.selectedVehicleOption.value,
      ) || formData.selectedVehicleOption
    );
  })();

  // Label look-ups used only for view-mode display of id-backed fields.
  const plantLabel =
    plantDataOption.find((option) => option.value === formData?.plant)?.label ||
    '';
  const workflowLabel =
    workflowDataOption.find((option) => option.value === formData?.workflowId)
      ?.label || '';
  const unitOfMeasurementLabel =
    unitOfMeasurementOptions?.find(
      (option) => option.value === formData?.unitOfMeasurementId,
    )?.label || '';
  const conditionLabel = (value) =>
    choices.find((choice) => String(choice.id) === String(value))?.name || '';

  const vehicleTrackingDetailComponents = [
    {
      id: 1,
      title: 'Basic Details',
      component: (
        <BasicDetails
          formData={formData}
          isEditing={isEditing}
          errorField={errorField}
          isTransporterInvalid={isTransporterInvalid}
          isVehicleFormFieldEmpty={isVehicleFormFieldEmpty}
          businessPartnerDataOption={businessPartnerDataOption}
          transporterValue={transporterValue}
          transporterSearchTerm={transporterSearchTerm}
          setTransporterSearchTerm={setTransporterSearchTerm}
          setFormData={setFormData}
          handleOnChange={handleOnChange}
          handleVehicleChange={handleVehicleChange}
          setVehicleSearchTerm={setVehicleSearchTerm}
          fetchVehicleDropdownOptions={fetchVehicleDropdownOptions}
          vehicleNumberDataOption={vehicleNumberDataOption}
          vehicleValue={vehicleValue}
          plantDataOption={plantDataOption}
          workflowDataOption={workflowDataOption}
          plantLabel={plantLabel}
          workflowLabel={workflowLabel}
        />
      ),
    },
    {
      id: 2,
      title: 'Additional Details',
      component: (
        <AdditionalDetails
          formData={formData}
          isEditing={isEditing}
          handleOnChange={handleOnChange}
          mobileNumberError={mobileNumberError}
          themeColor={themeColor}
          unitOfMeasurementOptions={unitOfMeasurementOptions}
          setUnitOfMeasurementSearchTerm={setUnitOfMeasurementSearchTerm}
          unitOfMeasurementLabel={unitOfMeasurementLabel}
          choices={choices}
          conditionLabel={conditionLabel}
        />
      ),
    },
  ];

  return (
    <>
      <div className="div-flex-column">
        <TabCreateView
          createLeadFormComponents={vehicleTrackingDetailComponents}
        />
      </div>
      <div className=" div-flex-row div-space-between div-align-center vertical-padding-10 horizontal-padding-10 border-top">
        {isEditing ? (
          <button
            type="button"
            className={`common-btn-outlined theme-border-color-${themeColor}`}
            onClick={handleCancelEdit}
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className={`common-btn-outlined theme-border-color-${themeColor}`}
            onClick={handleEdit}
          >
            Edit
          </button>
        )}

        {isEditing ? (
          <button
            type="button"
            className={`common-btn theme-background-${themeColor}`}
            onClick={handleSubmitEdit}
          >
            Submit
          </button>
        ) : (
          <button
            type="button"
            className={`common-btn theme-background-${themeColor}`}
            onClick={handleNextStep}
            disabled={selectedStep === totalSteps}
          >
            Next
          </button>
        )}
      </div>
    </>
  );
}

export default VehicleTrackingDetails;
